import { useNavigate } from 'react-router';

import { controlMachine } from '@/config/controlStates';
import { useControlContext } from '@/contexts/ControlContext';
import { getUserInfo } from '@/lib/auth';
import { apiService } from '@/lib/axios';

import { useNotificationModal } from './useNotificationModal';

const UNIT_KEYS = ['mgt', 'qa', 'ow', 'th'];

const parseUnits = (units = {}) => {
  return {
    other: units.others || '',
    selected: units.all ? ['all'] : UNIT_KEYS.filter((key) => units[key]),
  };
};

const parseUserList = (value = []) => {
  const rows = Array.isArray(value) ? value : value ? [value] : [];

  return rows.map((item) => item?.value?.sub || item?.sub).filter(Boolean);
};

const RISK_ASSESSMENT_MAP = {
  riskManagement: 'management',
  riskProcess: 'process',
  riskRegulations: 'regulations',
  riskSpecification: 'specification',
  riskSafety: 'safety',
};

const parseRiskAssessment = (payload = {}) =>
  Object.entries(RISK_ASSESSMENT_MAP)
    .filter(
      ([sourceKey]) => payload[sourceKey] === 1 || payload[sourceKey] === '1'
    )
    .map(([, targetKey]) => targetKey);

const CONTROL_REVIEW_GROUP_ORDER = [
  'processOwners',
  'qmrQa',
  'managementApprovers',
];

const buildReviewerPipeline = (reviewers) => {
  if (!reviewers) return [];

  return CONTROL_REVIEW_GROUP_ORDER.flatMap((group) => reviewers[group] || []);
};

const REQUIRED_APPROVAL_GROUPS = [
  ['qmrQa', 'QMR/QA reviewer'],
  ['processOwners', 'Process Owner reviewer'],
  ['managementApprovers', 'Management approver'],
];

const payloadCreateControl = (form, creator) => {
  const reviewers = {
    managementApprovers: parseUserList(form.approvalsPersonnel),
    processOwners: parseUserList(form.approvalsOwner),
    qmrQa: parseUserList(form.approvalsAssigned),
  };
  const reviewerPipeline = buildReviewerPipeline(reviewers);

  return {
    affectedUnits: parseUnits(form.informationUnits),
    detailChanges: form.detailChanges,
    readWithinDays: Number(form.informationReadWithin),
    reason: form.informationReason,
    requestorId: creator?.sub,
    reviewers,
    reviewerPipeline,
    risk: {
      assessment: parseRiskAssessment(form),
      attachment: form.riskAttachment || '',
      description: form.riskDescribe || '',
      residual: Number(form.riskResidual),
    },
    training: {
      deadline: form.trainingDeadline,
      information: form.trainingInformation,
      records: form.trainingRecords,
      required: form.trainingRequired === 1,
      units: parseUnits(form.trainingAttendees),
    },
    typeChange: form.typeChange || 'NEW_DOCUMENT',
  };
};

const useControlActions = () => {
  const navigate = useNavigate();

  const { form, refetch, reviews = [], setFormByKey } = useControlContext();
  const { id } = form;

  const { showNotificationModal } = useNotificationModal();

  const validateApprovals = () => {
    const reviewers = {
      qmrQa: parseUserList(form.approvalsAssigned),
      processOwners: parseUserList(form.approvalsOwner),
      managementApprovers: parseUserList(form.approvalsPersonnel),
    };
    const missingGroups = REQUIRED_APPROVAL_GROUPS.filter(
      ([key]) => !reviewers?.[key]?.length
    ).map(([, label]) => label);

    if (missingGroups.length) {
      showNotificationModal(
        'Cannot Submit',
        `Please assign at least one ${missingGroups.join(', ')} before submitting.`,
        'error'
      );

      return false;
    }

    return true;
  };

  const createControl = async () => {
    if (!validateApprovals()) return;

    try {
      const creator = getUserInfo();
      const payload = payloadCreateControl(form, creator);

      const response = await apiService.controls.submitNew(payload);

      if (response.data.success) {
        navigate('/controls', { state: 'pending' });

        showNotificationModal(
          'Submitted',
          `Change control submitted successfully. Change control number: ${response.data.data?.serial || response.data.data?.id}`,
          'success'
        );
      } else
        throw new Error(
          response.data.error || 'Failed to create change control'
        );
    } catch (error) {
      console.error('Error creating change control:', error);

      showNotificationModal(
        'Create Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to create change control',
        'error'
      );
    }
  };

  const createDraft = async () => {
    try {
      const creator = getUserInfo();
      const payload = payloadCreateControl(form, creator);

      const response = await apiService.controls.create({
        ...payload,
        isDraft: true,
      });

      if (response.data.success) {
        navigate('/controls', { state: 'pending' });
      } else
        throw new Error(
          response.data.error || 'Failed to draft change control'
        );
    } catch (error) {
      console.error('Error drafting change control:', error);

      showNotificationModal(
        'Draft Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to draft change control',
        'error'
      );
    }
  };

  const saveDraft = async () => {
    try {
      const creator = getUserInfo();
      const payload = payloadCreateControl(form, creator);

      const response = await apiService.controls.update(id, payload);

      if (response.data.success) {
        navigate('/controls', { state: 'pending' });
      } else
        throw new Error(response.data.error || 'Failed to save change control');
    } catch (error) {
      console.error('Error saving change control:', error);

      showNotificationModal(
        'Save Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to save change control',
        'error'
      );
    }
  };

  const deleteDraft = async () => {
    try {
      const response = await apiService.controls.delete(id);

      if (response.data.success) {
        navigate('/controls', { state: 'pending' });
        showNotificationModal(
          'Draft Deleted',
          'Change control draft deleted successfully.',
          'success'
        );
      } else
        throw new Error(
          response.data.error || 'Failed to delete change control draft'
        );
    } catch (error) {
      console.error('Error deleting change control draft:', error);

      showNotificationModal(
        'Delete Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to delete change control draft',
        'error'
      );
    }
  };

  const submitDraft = async () => {
    if (!validateApprovals()) return;

    try {
      const creator = getUserInfo();
      const payload = payloadCreateControl(form, creator);

      const response = await apiService.controls.submit(id, payload);

      if (response.data.success) {
        navigate('/controls', { state: 'pending' });
      } else
        throw new Error(
          response.data.error || 'Failed to submit change control'
        );
    } catch (error) {
      console.error('Error submitting change control:', error);

      showNotificationModal(
        'Submit Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to submit change control',
        'error'
      );
    }
  };

  const deleteRecord = async (recordId) => {
    try {
      const filesPayload = (form.trainingRecords || []).filter(
        ({ key }) => key !== recordId
      );

      const creator = getUserInfo();
      const controlPayload = payloadCreateControl(form, creator);

      const recordsResponse = await apiService.controls.update(id, {
        training: {
          ...controlPayload.training,
          records: filesPayload,
        },
      });

      if (recordsResponse.data.success) {
        refetch();
        setFormByKey('recordInput', null);
        showNotificationModal(
          'Additional Training Updated',
          `The training record have been successfully deleted.`
        );
      } else
        throw new Error(
          recordsResponse.data.error || 'Failed to delete the training record'
        );
    } catch (error) {
      console.error('Error deleting training record:', error);

      showNotificationModal(
        'Update Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to delete the training record',
        'error'
      );
    }
  };

  const updateRecords = async () => {
    try {
      const fileResponses = await Promise.all(
        form.recordInput.map((file) => apiService.uploads(file))
      );

      const filesPayload = fileResponses
        .map(({ data }) => ({
          key: data?.data?.key,
          originalName: data?.data?.originalName,
        }))
        .filter(Boolean);

      const creator = getUserInfo();
      const controlPayload = payloadCreateControl(form, creator);

      fileResponses.forEach(({ data }) => {
        if (!data?.success) throw new Error(data.error || 'Failed to upload');
      });

      const recordsResponse = await apiService.controls.update(id, {
        training: {
          ...controlPayload.training,
          records: [
            ...(controlPayload.training?.records || []),
            ...(filesPayload || []),
          ],
        },
      });

      if (recordsResponse.data.success) {
        refetch();
        setFormByKey('recordInput', null);
        showNotificationModal(
          'Additional Training Updated',
          `The training records have been successfully updated.`
        );
      } else
        throw new Error(
          recordsResponse.data.error || 'Failed to update training records'
        );
    } catch (error) {
      console.error('Error updating training records:', error);

      showNotificationModal(
        'Update Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to update training records',
        'error'
      );
    }
  };

  const completeProgression = async () => {
    try {
      const response = await apiService.controls.completeTraining(id);

      if (response.data.success) {
        refetch();
        showNotificationModal(
          'Additional Training Completed',
          'The additional training has been successfully marked as completed.'
        );
      } else
        throw new Error(
          response.data.error || 'Failed to complete change control progression'
        );
    } catch (error) {
      console.error('Error completing change control progression:', error);

      showNotificationModal(
        'Update Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to complete change control progression',
        'error'
      );
    }
  };

  const approve = async () => {
    try {
      const reviewerPipeline =
        form.reviewerPipeline || buildReviewerPipeline(form.reviewers);
      const resolved = controlMachine.resolve(
        form.status,
        'approve',
        form.currentActorIndex ?? 0,
        reviewerPipeline.length
      );

      if (!resolved)
        throw new Error('Failed to resolve change control approval');

      const response = await apiService.controls.approve(id, {
        ...form,
        ...resolved,
        reviewerPipeline,
      });

      if (response.data.success) {
        showNotificationModal(
          'Approved',
          'Change control approved.',
          'success'
        );
        navigate('/controls', { state: 'pending' });
      } else
        throw new Error(
          response.data.error || 'Failed to approve change control'
        );
    } catch (error) {
      console.error('Error approving change control:', error);

      showNotificationModal(
        'Approve Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to approve change control',
        'error'
      );
    }
  };

  const reject = async (reason) => {
    try {
      const reviewerPipeline =
        form.reviewerPipeline || buildReviewerPipeline(form.reviewers);

      const response = await apiService.controls.reject(id, {
        ...form,
        reviewerPipeline,
        reason,
        rejectedBy: getUserInfo()?.sub,
      });

      if (response.data.success) {
        showNotificationModal('Rejected', 'Change control rejected.', 'info');
        navigate('/controls', { state: 'pending' });
      } else
        throw new Error(
          response.data.error || 'Failed to reject change control'
        );
    } catch (error) {
      console.error('Error rejecting change control:', error);

      showNotificationModal(
        'Reject Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to reject change control',
        'error'
      );
    }
  };

  const returnToRequestor = async () => {
    try {
      const { sub, name } = getUserInfo();

      for (const { tab, text, entityId: commentEntityId } of reviews) {
        if (commentEntityId) continue;

        await apiService.comments.create({
          entityType: 'Control',
          entityId: id,
          entityRevision: form.documentRevision,
          tab,
          text,
          authorSub: sub,
          authorName: name,
        });
      }

      await apiService.comments.bulkFreeze({
        entityType: 'Control',
        entityId: id,
        entityRevision: form.documentRevision,
      });

      const reviewerPipeline =
        form.reviewerPipeline || buildReviewerPipeline(form.reviewers);
      const response = await apiService.controls.return(id, {
        ...form,
        comments: reviews,
        reviewerPipeline,
      });

      if (response.data.success) {
        showNotificationModal(
          'Returned',
          'Comments sent to requestor.',
          'info'
        );
        navigate('/controls', { state: 'pending' });
      } else
        throw new Error(
          response.data.error || 'Failed to return change control'
        );
    } catch (error) {
      console.error('Error returning change control:', error);

      showNotificationModal(
        'Return Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to return change control',
        'error'
      );
    }
  };

  return {
    approve,
    completeProgression,
    createControl,
    createDraft,
    deleteDraft,
    deleteRecord,
    reject,
    returnToRequestor,
    saveDraft,
    submitDraft,
    updateRecords,
  };
};

export default useControlActions;
